import type { BlobImageResult } from "./useImages";
import { isBlobImage } from "./useImages";

// ---------------------------------------------------------------------------
// In-memory LRU cache for blob image responses
// ---------------------------------------------------------------------------
// Images fetched from Azure Blob Storage (via the GetBlobImages function) are
// converted to base64 data URIs on the server and can be several MB each.
// This module caches successful responses so that navigating back/forward in
// the image viewer doesn't re-fetch the same blob from storage.
//
// Design choices:
//  • Module-level singleton — shared by every useImages() call in the app.
//  • LRU (least-recently-used) eviction keeps memory bounded.  At ~2 MB per
//    typical JPEG, 50 entries ≈ 100 MB — a reasonable ceiling for a browser
//    tab.  Adjust MAX_ENTRIES if needed.
//  • Only *successful* results are cached.  Errors (404, 403, transient) are
//    excluded so the user can retry and the system can recover.
//  • No TTL: blob URLs are immutable (same URL = same content), so there is
//    no need for time-based invalidation within a single session.
//  • Map iteration order equals insertion order in ES2015+, so the oldest
//    entry is always first.  To mark a key as "recently used" we delete and
//    re-insert it ("move to back"), which is O(1).
// ---------------------------------------------------------------------------

/** Maximum number of cached images before the least-recently-used is evicted. */
const MAX_ENTRIES = 50;

/**
 * The cache store.  Keys are blob URLs; values are successful BlobImageResult
 * objects (guaranteed to satisfy `isBlobImage()`).
 */
const cache = new Map<string, BlobImageResult>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Retrieve a previously cached image result for `url`.
 * Returns `undefined` on a cache miss.
 *
 * On a cache hit the entry is "touched" (moved to the back of the Map) so
 * it becomes the most-recently-used and won't be evicted next.
 */
export function getCached(url: string): BlobImageResult | undefined {
  const entry = cache.get(url);
  if (entry === undefined) return undefined;

  // Touch: delete and re-insert to move to the end (most-recently-used).
  cache.delete(url);
  cache.set(url, entry);

  return entry;
}

/**
 * Store a successful image result in the cache.
 *
 * Error results are intentionally *not* cached so that transient failures
 * (network hiccups, 503s) don't permanently block a URL.
 */
export function setCached(url: string, result: BlobImageResult): void {
  // Only cache successful downloads — errors should be retryable.
  if (!isBlobImage(result)) return;

  // If the URL is already present, delete first so re-insertion moves it to
  // the back (most-recently-used position).
  if (cache.has(url)) {
    cache.delete(url);
  }

  cache.set(url, result);

  // Evict the least-recently-used entry (first key in the Map) if we've
  // exceeded the maximum size.
  if (cache.size > MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) {
      cache.delete(oldestKey);
    }
  }
}

/**
 * Remove all entries from the cache.  Useful for testing or if the user
 * triggers a manual data refresh.
 */
export function clearCache(): void {
  cache.clear();
}
